'use client';

/* eslint-disable react/jsx-props-no-spreading */
import '@/styles/checkbox.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-tooltip-5/dist/react-tooltip.css';
import 'swiper/css';
import '../features/compAssociation/popup.css';
import '../styles/acres-date-picker-input.css';
import '../styles/globals.css';

import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import * as Sentry from '@sentry/nextjs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { debounce } from 'lodash';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { MapProvider } from 'react-map-gl/maplibre';
import { useDispatch } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import SavedSelectionFileUploadModal from '@/components/common/file-upload-modal/savedSelectionFileUploadModal';
import {
  AcresFeatureFlags,
  GLOBAL_TOOLTIP,
  LOCAL_STORAGE_SIGN_UP_TRACKED,
  LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA,
  REDIRECT_FROM,
} from '@/constants/constants';
import { LOGIN_SIGNUP_DATA } from '@/constants/localStorage';
import {
  ACCEPT_TOS_URL,
  EMBED_URL,
  LOGIN_URL,
  LOGOUT_URL,
  MY_ACRES_URL,
  PLAT_MAP_URL,
} from '@/constants/urls';
import { DeedPlotterProvider } from '@/features/boundaries/deedPlotter/provider';
import BoundariesProvider from '@/features/boundaries/provider';
import { TrackingConsentModal } from '@/features/consentBanner/TrackingConsentModal';
import CustomizeLandProvider from '@/features/customizeMyLand/provider';
import { useGoogleClickId } from '@/features/gclid/useGoogleClickId';
import WaterInfoModal from '@/features/insights/water/waterInfoModal';
import { InsightsCatalogProvider } from '@/features/insightsCatalog/provider';
import { LayerLibraryProvider } from '@/features/layerLibrary/provider';
import GlobalModal from '@/features/modal/globalModal';
import UploadFileModal from '@/features/modal/uploadFileModal';
import { NavigationProvider } from '@/features/navigation/provider';
import { SelectionsPanelProvider } from '@/features/selectionsPanel/selectionsPanelProvider';
import { ToolbarProvider } from '@/features/toolbar/provider';
import { UserQuotasProvider } from '@/features/userQuotas/provider';
import useWebStorage from '@/hooks/useWebStorage';
import { AcresFeature } from '@/models/features';
import whoAmi from '@/ory/accountManagement/whoami';
import { FeatureHubSessionConfig } from '@/ory/models/session';
import { FileManagementProvider } from '@/providers/FileManagementProvider';
import InitializeProvider, {
  useInitializeContext,
} from '@/providers/InitializeProvider';
import { MapboxProvider } from '@/providers/mapbox/MapboxProvider';
import { ModalProvider } from '@/providers/ModalProvider';
import { ToastProvider, useToastContext } from '@/providers/ToastProvider';
import { ToolTipProvider } from '@/providers/ToolTipProvider';
import { addFeature } from '@/redux/slices/featureSlice';
import { shouldLogoutEarly } from '@/utils/auth';
import Logger from '@/utils/logger';

import {
  AnalyticsProvider,
  useAnalytics,
} from '../providers/AnalyticsProvider';
import { AuthUserProvider, useAuth } from '../providers/auth-provider';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const GlobalToolTip = dynamic(
  () => import('../components/common/tooltip/globalTooltip')
);
const ReduxToast = dynamic(() => import('../components/common/toast'));
const ReactToolTipNextWrapper = dynamic(
  () => import('../components/react-tooltip-next-wrapper')
);
const Tooltip = dynamic(() => import('../components/tooltip'));

let lastPageURL: string | undefined;

type LoginSignUpData = {
  features?: AcresFeature[];
};

function AppPage({ children = null }: PropsWithChildren<{}>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { track, trackPageView } = useAnalytics();
  const { oryLogout, session, authStateLoading, hasActiveSession } = useAuth();
  const { getFeature } = useInitializeContext();
  useGoogleClickId();

  useEffect(() => {
    if (session?.identity?.traits?.email) {
      Sentry.setUser({ email: session.identity.traits.email });
    } else {
      Sentry.setUser(null);
    }
  }, [session?.identity?.traits?.email]);

  const { savedValue: loginSignUpData, deleteValue } =
    useWebStorage<LoginSignUpData>({
      key: LOGIN_SIGNUP_DATA,
      useSessionStorage: true,
    });

  const { toastContent, toastPosition } = useToastContext();
  const orySessionConfig: FeatureHubSessionConfig = useMemo(
    () =>
      (getFeature(
        AcresFeatureFlags.ORY_SESSION_CONFIG
      ) as FeatureHubSessionConfig) || {},
    [getFeature]
  );

  const isOnPlatMap = useMemo(() => {
    if (!pathname) return false;
    const urlWithNoQuery = pathname.split('?')[0];
    return urlWithNoQuery.includes(PLAT_MAP_URL);
  }, [pathname]);

  useEffect(() => {
    if (!loginSignUpData?.features) return;
    dispatch(
      addFeature({
        feature: loginSignUpData.features,
      })
    );
    deleteValue();
  }, [deleteValue, dispatch, loginSignUpData, router]);

  useEffect(() => {
    if (lastPageURL === undefined) {
      lastPageURL = window.location.pathname;
      trackPageView();
    }

    const ignoreURLChange =
      lastPageURL === pathname && pathname.startsWith('/plat-map/map');
    if (!ignoreURLChange) {
      lastPageURL = pathname ?? undefined;
      trackPageView();
    }
  }, [trackPageView, pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || pathname?.includes(LOGOUT_URL)) return;

    const fromRoute = window.localStorage.getItem(REDIRECT_FROM);

    if (!fromRoute) return;

    const jsonParse = JSON.parse(fromRoute);

    window.location.href = jsonParse.from;

    window.localStorage.removeItem(REDIRECT_FROM);
  }, [router]);

  const getCurrSession = async () => {
    // fetching current session,
    // there is an issue where the useAuth doesn't update soon enough
    // causing the value to be old, even when successfully updated
    // open to better solutions here
    const currentSession = await whoAmi();

    if ('error' in currentSession) {
      window.location.href = LOGIN_URL;
    } else if (!currentSession.identity.traits.tos) {
      // routes users can not access without TOS
      if (isOnPlatMap || pathname?.includes(MY_ACRES_URL)) {
        window.location.href = ACCEPT_TOS_URL;
      }
    }
  };

  // prevent user from accessing acres without TOS
  useEffect(() => {
    if (!session || authStateLoading || session.identity.traits.tos) return;

    if (!session.identity.traits.tos) {
      getCurrSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, session, authStateLoading]);

  const debouncedLogoutCheck = useMemo(
    () =>
      // debounced to avoid race condition
      debounce(async () => {
        if (!hasActiveSession) return;
        const now = Date.now();
        const currentSession = await whoAmi();
        if (
          session &&
          (shouldLogoutEarly(orySessionConfig, session, now) ||
            'error' in currentSession)
        ) {
          // logout and prompt login
          oryLogout();

          window.location.href = `/login?from=${
            pathname?.replace('/', '') ?? ''
          }`;
        }
      }, 1),
    [hasActiveSession, oryLogout, orySessionConfig, pathname, session]
  );

  // Check if user should be logged out of session early
  useEffect(() => {
    const intervalId = setInterval(debouncedLogoutCheck, 1000 * 10); // Checking every 10 seconds

    return () => {
      clearInterval(intervalId);
    };
  });

  useEffect(() => {
    try {
      // if socialSignupData exists, then the user must have logged in or signed up with social account
      const socialSignupData = window.localStorage.getItem(
        LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA
      );

      if (socialSignupData === null || !session) return;

      const signUpTracked = window.localStorage.getItem(
        LOCAL_STORAGE_SIGN_UP_TRACKED
      );

      const parsedData = JSON.parse(socialSignupData);
      const { isSignUpFlow } = parsedData;
      const isNewUser = !session.identity.traits.tos; // hasn't accepted Terms of Service so must be new user

      if (isSignUpFlow === false && !isNewUser) {
        track({ data: {}, event: TrackingEvent.SIGNED_IN });
      } else if (signUpTracked === null) {
        delete parsedData.isSignUpFlow;
        window.localStorage.setItem(LOCAL_STORAGE_SIGN_UP_TRACKED, 'true');
        track({ data: {}, event: TrackingEvent.SIGN_UP_COMPLETED });
      }

      window.localStorage.removeItem(LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA);
    } catch (error) {
      if (error && !error?.message?.includes('localStorage is not available')) {
        Logger.error(error);
      }
    }
  }, [track, session]);

  return (
    <>
      <SavedSelectionFileUploadModal />
      {children}
      <GlobalToolTip />
      <WaterInfoModal />
      <GlobalModal />
      <ReduxToast />
      <ReactToolTipNextWrapper />
      <UploadFileModal />
      <TrackingConsentModal />
      <Tooltip className="overflow-hidden break-words" id={GLOBAL_TOOLTIP} />
      {toastContent && (
        <div
          // z-index is one above the Acres Modal
          className={twMerge(
            toastPosition === 'bottom' ? 'bottom-8' : 'top-8',
            'fixed left-0 z-[1001] flex h-[48px] w-screen items-center justify-center',
            'pointer-events-none'
          )}
        >
          <div className="pointer-events-auto">{toastContent}</div>
        </div>
      )}
    </>
  );
}

function ClientLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  const isOnEmbed = useMemo(() => {
    if (!pathname) return false;
    const urlWithNoQuery = pathname.split('?')[0];
    return urlWithNoQuery.includes(EMBED_URL);
  }, [pathname]);

  return (
    <MapProvider>
      <ToastProvider toastPosition={!isOnEmbed ? 'bottom' : 'top'}>
        <AnalyticsProvider>
          <AuthUserProvider>
            <InitializeProvider>
              <ToolTipProvider>
                <UserQuotasProvider>
                  <MapboxProvider>
                    <ModalProvider>
                      <FileManagementProvider>
                        <NavigationProvider>
                          <ToolbarProvider>
                            <BoundariesProvider>
                              <CustomizeLandProvider>
                                <DeedPlotterProvider>
                                  <LayerLibraryProvider>
                                    <InsightsCatalogProvider>
                                      <SelectionsPanelProvider>
                                        <AppPage>{children}</AppPage>
                                      </SelectionsPanelProvider>
                                    </InsightsCatalogProvider>
                                  </LayerLibraryProvider>
                                </DeedPlotterProvider>
                              </CustomizeLandProvider>
                            </BoundariesProvider>
                          </ToolbarProvider>
                        </NavigationProvider>
                      </FileManagementProvider>
                    </ModalProvider>
                  </MapboxProvider>
                </UserQuotasProvider>
              </ToolTipProvider>
            </InitializeProvider>
          </AuthUserProvider>
        </AnalyticsProvider>
      </ToastProvider>
    </MapProvider>
  );
}

export default ClientLayout;
